(() => {
  if (window.MeubelistaWishlist) return;

  const cfg = window.MeubelistaWishlistConfig || {};
  const STORAGE_KEY = 'meubelista-wishlist-v1';
  const NAME_KEY = 'meubelista-first-name';
  const MAX_ITEMS = 60;
  const CHANGE_EVENT = 'meubelista:wishlist-change';
  const NAME_EVENT = 'meubelista:name-change';

  let memoryItems = [];
  let memoryName = '';

  function absoluteUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return 'https:' + url;
    if (url.startsWith('/')) return window.location.origin + url;
    return url;
  }

  function cleanName(value) {
    if (typeof value !== 'string') return '';
    const name = value.replace(/[\u0000-\u001f\u007f<>]/g, '').replace(/\s+/g, ' ').trim();
    if (!name || name.length > 50) return '';
    return name;
  }

  function sanitizeItem(raw) {
    if (!raw || typeof raw !== 'object' || typeof raw.h !== 'string' || !raw.h) return null;
    return {
      h: raw.h,
      p: Number(raw.p) || 0,
      v: Number(raw.v) || 0,
      t: String(raw.t || ''),
      vt: String(raw.vt || ''),
      u: String(raw.u || ''),
      i: String(raw.i || ''),
      pr: Number(raw.pr) || 0,
      cp: Number(raw.cp) || 0,
      ty: String(raw.ty || ''),
      qs: Math.min(12, Math.max(1, parseInt(raw.qs, 10) || 1)),
      qp: raw.qp && raw.qp !== '0' ? 1 : 0,
      a: Number(raw.a) || Date.now()
    };
  }

  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const template = String(format || cfg.moneyFormat || '€{{amount_with_comma_separator}}').replace(/<[^>]*>/g, '');

    const fmt = (amount, decimals, thousands, decimalSep) => {
      const fixed = (amount / 100).toFixed(decimals);
      const parts = fixed.split('.');
      const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? integer + decimalSep + parts[1] : integer;
    };

    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(value, 2, ',', '.');
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_period_and_space_separator': return fmt(value, 2, ' ', '.');
        default: return fmt(value, 2, '.', ',');
      }
    });
  }

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(sanitizeItem).filter(Boolean) : [];
    } catch (error) {
      return memoryItems.slice();
    }
  }

  function write(items, detail) {
    const clean = items.map(sanitizeItem).filter(Boolean).slice(0, MAX_ITEMS);
    memoryItems = clean;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
    } catch (error) {
    }
    emit(Object.assign({ items: clean }, detail || {}));
    scheduleProfileSync();
    return clean;
  }

  function emit(detail) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: detail || { items: read() } }));
  }

  function has(handle) {
    return read().some((item) => item.h === handle);
  }

  function count() {
    return read().length;
  }

  function add(rawItem, options) {
    const item = sanitizeItem(Object.assign({ a: Date.now() }, rawItem));
    if (!item) return read();
    const items = read().filter((existing) => existing.h !== item.h);
    items.unshift(item);
    const result = write(items, { action: 'add', item });
    if (!(options && options.silent)) trackItem('Added to Wishlist', item, result.length);
    return result;
  }

  function addMany(rawItems) {
    const incoming = rawItems.map((raw) => sanitizeItem(Object.assign({ a: Date.now() }, raw))).filter(Boolean);
    const handles = new Set(incoming.map((item) => item.h));
    const items = incoming.concat(read().filter((existing) => !handles.has(existing.h)));
    const result = write(items, { action: 'add-many', items: incoming });
    incoming.forEach((item) => trackItem('Added to Wishlist', item, result.length));
    return result;
  }

  function remove(handle, options) {
    const current = read();
    const item = current.find((existing) => existing.h === handle);
    if (!item) return current;
    const result = write(current.filter((existing) => existing.h !== handle), { action: 'remove', item });
    if (!(options && options.silent)) trackItem('Removed from Wishlist', item, result.length);
    return result;
  }

  function restore(item, index) {
    const clean = sanitizeItem(item);
    if (!clean) return read();
    const items = read().filter((existing) => existing.h !== clean.h);
    items.splice(Math.max(0, Math.min(index ?? 0, items.length)), 0, clean);
    const result = write(items, { action: 'add', item: clean });
    trackItem('Added to Wishlist', clean, result.length);
    return result;
  }

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) emit({ items: read(), action: 'sync' });
    if (event.key === NAME_KEY) document.dispatchEvent(new CustomEvent(NAME_EVENT, { detail: { name: getName() } }));
  });

  function getName() {
    const customerName = cleanName(cfg.customerFirstName || '');
    if (customerName) return customerName;
    try {
      return cleanName(localStorage.getItem(NAME_KEY) || '') || memoryName;
    } catch (error) {
      return memoryName;
    }
  }

  function setName(value) {
    const name = cleanName(value);
    if (!name) return;
    memoryName = name;
    try {
      localStorage.setItem(NAME_KEY, name);
    } catch (error) {
    }
    document.dispatchEvent(new CustomEvent(NAME_EVENT, { detail: { name: getName() } }));
  }

  function shareUrl(items) {
    const list = (items || read()).map((item) => `${item.h}:${item.v || ''}`).join(',');
    const url = new URL(cfg.pageUrl || '/pages/verlanglijstje', window.location.origin);
    if (list) url.searchParams.set('lijst', list);
    const name = getName();
    if (name) url.searchParams.set('van', name);
    return url.toString();
  }

  function klaviyoPush(args) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function' && !Array.isArray(window.klaviyo)) {
        window.klaviyo.push(args);
      } else {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(args);
      }
    } catch (error) {
    }
  }

  function itemPayload(item) {
    return {
      ProductName: item.t,
      ProductID: item.p ? String(item.p) : '',
      VariantID: item.v ? String(item.v) : '',
      VariantName: item.vt,
      URL: absoluteUrl(item.u),
      ImageURL: absoluteUrl(item.i),
      Price: item.pr / 100,
      CompareAtPrice: item.cp ? item.cp / 100 : null,
      Categories: item.ty ? [item.ty] : []
    };
  }

  function trackItem(eventName, item, total) {
    klaviyoPush(['track', eventName, Object.assign(itemPayload(item), {
      $value: item.pr / 100,
      WishlistCount: total,
      WishlistURL: shareUrl()
    })]);
  }

  let syncTimer = null;

  function scheduleProfileSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncProfile, 1200);
  }

  function syncProfile() {
    const items = read();
    const properties = {
      'Wishlist Count': items.length,
      'Wishlist Product Names': items.map((item) => item.t),
      'Wishlist Product URLs': items.map((item) => absoluteUrl(item.u)),
      'Wishlist Product IDs': items.map((item) => String(item.p)),
      'Wishlist URL': shareUrl(items),
      'Wishlist Updated': new Date().toISOString()
    };

    const k = window.klaviyo;

    if (k && typeof k.isIdentified === 'function') {
      Promise.resolve(k.isIdentified())
        .then((identified) => { if (identified) k.identify(properties); })
        .catch(() => {});
    } else {
      klaviyoPush(['identify', properties]);
    }
  }

  function identifyAndTrack(profile, eventName, eventProperties) {
    klaviyoPush(['identify', profile]);
    klaviyoPush(['track', eventName, eventProperties]);
  }

  let toastElement = null;
  let toastTimer = null;

  function toast(message, action) {
    if (!toastElement) {
      toastElement = document.createElement('div');
      toastElement.className = 'mb-wl-toast';
      toastElement.setAttribute('role', 'status');
      toastElement.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastElement);
    }

    toastElement.textContent = '';
    const text = document.createElement('span');
    text.textContent = message;
    toastElement.appendChild(text);

    if (action) {
      const control = document.createElement(action.href ? 'a' : 'button');
      control.className = 'mb-wl-toast__action';
      control.textContent = action.label;
      if (action.href) {
        control.href = action.href;
      } else {
        control.type = 'button';
        control.addEventListener('click', () => {
          action.onClick();
          hideToast();
        });
      }
      toastElement.appendChild(control);
    }

    placeToast();
    if (!toastFollowing) {
      toastFollowing = true;
      window.addEventListener('scroll', followBars, { passive: true });
      window.addEventListener('resize', followBars, { passive: true });
    }
    requestAnimationFrame(() => toastElement.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4200);
  }

  function hideToast() {
    if (toastElement) toastElement.classList.remove('is-visible');
    if (toastFollowing) {
      toastFollowing = false;
      window.removeEventListener('scroll', followBars);
      window.removeEventListener('resize', followBars);
    }
  }

  const BOTTOM_BARS = '.mb-satc, .mb-cart-sticky, .bvj-bar';
  let toastFollowing = false;
  let followFrame = 0;
  let followTimer = 0;

  function placeToast() {
    if (!toastElement) return;
    const viewport = window.innerHeight;
    const own = toastElement.getBoundingClientRect();
    let lift = 0;
    document.querySelectorAll(BOTTOM_BARS).forEach((bar) => {
      if (bar.hidden) return;
      const style = getComputedStyle(bar);
      if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) < 0.1) return;
      const rect = bar.getBoundingClientRect();
      if (!rect.height || rect.top >= viewport || rect.bottom <= 0) return;
      if (rect.right <= own.left || rect.left >= own.right) return;
      lift = Math.max(lift, viewport - rect.top);
    });
    toastElement.style.bottom = lift ? `${Math.ceil(lift + 12)}px` : '';
  }

  function followBars() {
    cancelAnimationFrame(followFrame);
    followFrame = requestAnimationFrame(placeToast);
    clearTimeout(followTimer);
    followTimer = setTimeout(placeToast, 220);
  }

  class MbWishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button || this._ready) return;
      this._ready = true;

      this._onClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      };
      this._onChange = () => this.sync();
      this._onVariant = (event) => {
        const variant = event.detail && event.detail.variant;
        if (!variant) return;
        this.dataset.variantId = variant.id;
        this.dataset.price = variant.price;
        this.dataset.compareAtPrice = variant.compare_at_price || 0;
        if (this.dataset.multipleVariants === 'true') this.dataset.variantTitle = variant.title || '';
        const image = variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src;
        if (image) this.dataset.image = image + (image.includes('?') ? '&' : '?') + 'width=600';
      };

      this.button.addEventListener('click', this._onClick);
      document.addEventListener(CHANGE_EVENT, this._onChange);

      if (this.dataset.form) {
        this._form = document.forms[this.dataset.form];
        if (this._form) this._form.addEventListener('variant:change', this._onVariant);
      }

      this.sync();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.button.removeEventListener('click', this._onClick);
      document.removeEventListener(CHANGE_EVENT, this._onChange);
      if (this._form) this._form.removeEventListener('variant:change', this._onVariant);
    }

    get item() {
      return {
        h: this.dataset.handle,
        p: this.dataset.productId,
        v: this.dataset.variantId,
        t: this.dataset.title,
        vt: this.dataset.variantTitle,
        u: this.dataset.url,
        i: this.dataset.image,
        pr: this.dataset.price,
        cp: this.dataset.compareAtPrice,
        ty: this.dataset.type,
        qs: this.dataset.qtyStep,
        qp: this.hasAttribute('data-qty-select') ? 1 : 0
      };
    }

    sync() {
      const active = has(this.dataset.handle);
      this.classList.toggle('is-active', active);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.button.setAttribute('aria-label', active ? 'Verwijder uit je verlanglijstje' : 'Bewaar in je verlanglijstje');
    }

    toggle() {
      const handle = this.dataset.handle;
      if (!handle) return;

      if (has(handle)) {
        const before = read();
        const index = before.findIndex((item) => item.h === handle);
        const removed = before[index];
        remove(handle);
        toast('Verwijderd uit je verlanglijstje', {
          label: 'Ongedaan maken',
          onClick: () => restore(removed, index)
        });
      } else {
        add(this.item);
        this.classList.remove('is-popping');
        void this.offsetWidth;
        this.classList.add('is-popping');
        toast('Bewaard in je verlanglijstje', { label: 'Bekijken', href: cfg.pageUrl || '/pages/verlanglijstje' });
      }
    }
  }

  class MbWishlistCount extends HTMLElement {
    connectedCallback() {
      this._onChange = (event) => this.render(event && event.detail && event.detail.action === 'add');
      document.addEventListener(CHANGE_EVENT, this._onChange);
      this.render(false);
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
    }

    render(bump) {
      const total = count();
      this.textContent = total > 99 ? '99+' : String(total);
      this.classList.toggle('opacity-0', total === 0);
      const link = this.closest('[data-mb-wishlist-link]');
      if (link) link.setAttribute('aria-label', total ? `Verlanglijstje, ${total} ${total === 1 ? 'product' : 'producten'}` : 'Verlanglijstje');
      if (bump && link) heartBeat(link);
    }
  }

  const HEART_PATH = 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21.2l7.8-7.7 1-1.1a5.5 5.5 0 0 0 0-7.8z';

  function reducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function heartBeat(link) {
    const icon = link.querySelector('.mb-wishlist-header-icon');
    if (!icon || typeof icon.animate !== 'function' || reducedMotion()) return;
    if (icon._mbBeat) icon._mbBeat.forEach((animation) => animation.cancel());

    const ink = getComputedStyle(icon).color || '#121212';
    const glow = 'drop-shadow(0 0 6px rgba(232, 117, 138, .8))';
    const noGlow = 'drop-shadow(0 0 0 rgba(232, 117, 138, 0))';
    icon._mbBeat = [
      icon.animate([
        { fill: 'rgba(170, 40, 38, 0)', stroke: ink },
        { fill: '#aa2826', stroke: '#aa2826', offset: 0.2 },
        { fill: '#aa2826', stroke: '#aa2826', offset: 0.75 },
        { fill: 'rgba(170, 40, 38, 0)', stroke: ink }
      ], { duration: 1300, easing: 'ease-out' }),
      icon.animate([
        { transform: 'scale(1)', filter: noGlow },
        { transform: 'scale(1.22)', filter: glow, offset: 0.15 },
        { transform: 'scale(1)', offset: 0.3 },
        { transform: 'scale(1.18)', filter: glow, offset: 0.45 },
        { transform: 'scale(1)', filter: noGlow }
      ], { duration: 900, easing: 'ease-in-out' })
    ];

    const rect = icon.getBoundingClientRect();
    if (!rect.width || rect.bottom < 0) return;
    const fx = document.createElement('span');
    fx.className = 'mb-heart-fx';
    fx.setAttribute('aria-hidden', 'true');
    fx.innerHTML = `<svg viewBox="0 0 24 24" width="12" height="12" focusable="false"><path d="${HEART_PATH}"/></svg>`;
    document.body.appendChild(fx);
    const x = rect.left + rect.width / 2 - 6;
    const y = rect.top + rect.height / 2 - 6;
    const float = fx.animate([
      { transform: `translate(${x}px, ${y}px) scale(.4)`, opacity: 0 },
      { transform: `translate(${x + 4}px, ${y - 22}px) scale(1)`, opacity: 1, offset: 0.35 },
      { transform: `translate(${x - 2}px, ${y - 46}px) scale(.85)`, opacity: 0 }
    ], { duration: 1200, delay: 650, easing: 'ease-out', fill: 'both' });
    float.onfinish = () => fx.remove();
    float.oncancel = () => fx.remove();
  }

  const CART_COUNT_KEY = 'meubelista-cart-count';

  function bundleRole(line) {
    const raw = line && line.properties && line.properties._bundleConfig;
    if (typeof raw !== 'string') return null;
    let config = null;
    try {
      config = JSON.parse(raw);
    } catch (error) {
      try {
        config = JSON.parse(raw.replace(/&quot;/g, '"'));
      } catch (innerError) {
        return null;
      }
    }
    if (!config || (config.role !== 'lamp' && config.role !== 'bulb')) return null;
    return { role: config.role, id: String(config.uniqueId) };
  }

  function cartProductCount(cart) {
    const lines = cart && Array.isArray(cart.items) ? cart.items : [];
    const total = cart && typeof cart.item_count === 'number'
      ? cart.item_count
      : lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
    const lamps = new Set();
    lines.forEach((line) => {
      const bundle = bundleRole(line);
      if (bundle && bundle.role === 'lamp') lamps.add(bundle.id);
    });
    let products = 0;
    lines.forEach((line) => {
      if (line.parent_relationship) return;
      const bundle = bundleRole(line);
      if (bundle && bundle.role === 'bulb' && lamps.has(bundle.id)) return;
      products += Number(line.quantity) || 0;
    });
    return products > 0 ? products : total;
  }

  class MbCartCount extends HTMLElement {
    connectedCallback() {
      this.link = this.closest('.mbh-cart') || this.parentElement;
      this.icon = this.link ? this.link.querySelector('.mb-cart-icon') : null;
      this.value = parseInt(this.getAttribute('data-count'), 10) || 0;
      this.abort = new AbortController();
      const signal = this.abort.signal;

      document.addEventListener('cart:change', (event) => {
        const cart = event.detail && event.detail.cart;
        if (cart && Array.isArray(cart.items)) this.set(cartProductCount(cart));
        else this.refresh();
      }, { signal });
      document.addEventListener('cart:refresh', () => this.refresh(), { signal });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refresh({ quiet: true });
      }, { signal });

      const verify = this.hasAttribute('data-verify');
      const from = this.getAttribute('data-arrive-from');
      if (from !== null) {
        this.removeAttribute('data-arrive-from');
        this.value = parseInt(from, 10) || 0;
        const target = parseInt(this.getAttribute('data-count'), 10) || 0;
        const play = () => setTimeout(() => (verify ? this.refresh() : this.set(target)), 320);
        if (document.visibilityState === 'hidden') {
          document.addEventListener('visibilitychange', play, { once: true, signal });
        } else {
          requestAnimationFrame(play);
        }
      } else if (verify) {
        this.refresh({ quiet: true });
      }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    async refresh(options) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        this.set(cartProductCount(await response.json()), options);
      } catch (error) {
      }
    }

    set(next, options) {
      const quiet = Boolean(options && options.quiet);
      const previous = this.value;
      this.value = next;
      this.setAttribute('data-count', String(next));
      try {
        sessionStorage.setItem(CART_COUNT_KEY, String(next));
      } catch (error) {
      }

      const boxesBefore = this.icon ? parseInt(this.icon.getAttribute('data-items'), 10) || 0 : 0;
      const boxes = Math.min(next, 3);
      if (this.icon) this.icon.setAttribute('data-items', String(boxes));
      this.classList.toggle('opacity-0', next === 0);

      const label = next > 99 ? '99+' : String(next);
      const motion = !quiet && !reducedMotion() && typeof this.animate === 'function';
      if (!motion || previous === 0 || next === 0 || previous === next) {
        this.current().textContent = label;
      } else {
        this.roll(label, next > previous);
      }
      if (motion && next > previous) this.celebrate(boxesBefore, boxes);
    }

    current() {
      const spans = Array.from(this.querySelectorAll('.mb-cart-num'));
      let span = spans.pop();
      spans.forEach((extra) => extra.remove());
      if (!span) {
        span = document.createElement('span');
        span.className = 'mb-cart-num';
        span.textContent = this.textContent.trim();
        this.textContent = '';
        this.appendChild(span);
      }
      if (span.getAnimations) span.getAnimations().forEach((animation) => animation.cancel());
      span.style.position = '';
      return span;
    }

    roll(label, up) {
      const old = this.current();
      if (old.textContent === label) return;
      const next = document.createElement('span');
      next.className = 'mb-cart-num';
      next.textContent = label;
      next.style.position = 'absolute';
      this.appendChild(next);
      const easing = 'cubic-bezier(.4, 0, .2, 1)';
      old.animate([
        { transform: 'translateY(0)', opacity: 1 },
        { transform: `translateY(${up ? -110 : 110}%)`, opacity: 0 }
      ], { duration: 260, easing, fill: 'forwards' });
      next.animate([
        { transform: `translateY(${up ? 110 : -110}%)`, opacity: 0 },
        { transform: 'translateY(0)', opacity: 1 }
      ], { duration: 260, easing }).onfinish = () => {
        old.remove();
        next.style.position = '';
      };
    }

    celebrate(boxesBefore, boxes) {
      let stagger = 0;
      if (this.icon) {
        for (let n = boxesBefore + 1; n <= boxes; n += 1) {
          const box = this.icon.querySelector(`.mb-cart-box--${n}`);
          if (box) {
            box.animate([
              { transform: 'translateY(-10px)', opacity: 0 },
              { transform: 'translateY(-6px)', opacity: 1, offset: 0.25 },
              { transform: 'translateY(.5px) scaleY(.86)', offset: 0.72 },
              { transform: 'translateY(0) scaleY(1)' }
            ], { duration: 420, delay: stagger, easing: 'cubic-bezier(.45, 0, .55, 1)', fill: 'backwards' });
          }
          stagger += 110;
        }
        this.icon.animate([
          { transform: 'rotate(0)' },
          { transform: 'rotate(-9deg)', offset: 0.25 },
          { transform: 'rotate(6deg)', offset: 0.55 },
          { transform: 'rotate(-2deg)', offset: 0.8 },
          { transform: 'rotate(0)' }
        ], { duration: 520, delay: 300 + Math.max(0, stagger - 110), easing: 'ease-in-out' });
      }
      this.animate([
        { transform: 'scale(1)' },
        { transform: 'scale(1.3)', offset: 0.4 },
        { transform: 'scale(1)' }
      ], { duration: 420, delay: 120, easing: 'ease-out' });
    }
  }

  window.MeubelistaWishlist = {
    read,
    has,
    count,
    add,
    addMany,
    remove,
    restore,
    getName,
    setName,
    shareUrl,
    formatMoney,
    absoluteUrl,
    itemPayload,
    identifyAndTrack,
    klaviyoPush,
    toast,
    config: cfg,
    CHANGE_EVENT,
    NAME_EVENT
  };

  if (!customElements.get('mb-wishlist-button')) customElements.define('mb-wishlist-button', MbWishlistButton);
  if (!customElements.get('mb-wishlist-count')) customElements.define('mb-wishlist-count', MbWishlistCount);
  if (!customElements.get('mb-cart-count')) customElements.define('mb-cart-count', MbCartCount);

  try {
    if (read().length && !sessionStorage.getItem('meubelista-wishlist-synced')) {
      sessionStorage.setItem('meubelista-wishlist-synced', '1');
      setTimeout(syncProfile, 4000);
    }
  } catch (error) {
  }
})();

(() => {
  if (customElements.get('mb-wishlist-page')) return;

  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9\-_]*$/i;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  }

  function cleanName(value) {
    if (typeof value !== 'string') return '';
    const name = value.replace(/[\u0000-\u001f\u007f<>]/g, '').replace(/\s+/g, ' ').trim();
    return name && name.length <= 50 ? name : '';
  }

  function sizedImage(url, width) {
    if (!url) return '';
    const base = url.startsWith('//') ? 'https:' + url : url;
    return base.replace(/([?&])width=\d+/, '$1').replace(/[?&]$/, '') + (base.includes('?') ? '&' : '?') + 'width=' + width;
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const QUANTITY_KEY = 'meubelista-wishlist-qty-v1';
  const QUANTITY_MAX = 96;

  function quantityStep(value) {
    const step = parseInt(value, 10);
    return step >= 1 && step <= 12 ? step : 1;
  }

  function normalizeQuantity(value, step) {
    const amount = parseInt(value, 10);
    if (!(amount >= step)) return step;
    const rounded = Math.round(amount / step) * step;
    return Math.min(Math.max(rounded, step), Math.floor(QUANTITY_MAX / step) * step);
  }

  function readQuantities() {
    try {
      const stored = JSON.parse(localStorage.getItem(QUANTITY_KEY) || '{}');
      return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
    } catch (error) {
      return {};
    }
  }

  class MbWishlistPage extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      if (window.MeubelistaWishlist) {
        this.init();
      } else {
        let attempts = 0;
        const retry = () => {
          if (window.MeubelistaWishlist) return this.init();
          if (attempts++ < 50) setTimeout(retry, 100);
        };
        retry();
      }
    }

    init() {
      this.W = window.MeubelistaWishlist;
      this.cfg = this.W.config || {};
      this.cache = new Map();
      this.renderToken = 0;
      this.quantities = readQuantities();

      this.el = {
        eyebrow: this.querySelector('[data-wl-eyebrow]'),
        title: this.querySelector('[data-wl-title]'),
        meta: this.querySelector('[data-wl-meta]'),
        shared: this.querySelector('[data-wl-shared]'),
        sharedText: this.querySelector('[data-wl-shared-text]'),
        saveShared: this.querySelector('[data-wl-save-shared]'),
        layout: this.querySelector('[data-wl-layout]'),
        loading: this.querySelector('[data-wl-loading]'),
        list: this.querySelector('[data-wl-list]'),
        empty: this.querySelector('[data-wl-empty]'),
        aside: this.querySelector('[data-wl-aside]'),
        total: this.querySelector('[data-wl-total]'),
        summaryLabel: this.querySelector('[data-wl-summary-label]'),
        unavailableNote: this.querySelector('[data-wl-unavailable-note]'),
        addAll: this.querySelector('[data-wl-add-all]'),
        share: this.querySelector('[data-wl-share]'),
        shareLabel: this.querySelector('[data-wl-share-label]'),
        mailForm: this.querySelector('[data-wl-mail-form]'),
        mailStatus: this.querySelector('[data-wl-mail-status]'),
        nameInput: this.querySelector('[data-wl-name-input]'),
        consent: this.querySelector('[data-wl-consent]')
      };

      const params = new URLSearchParams(window.location.search);
      const sharedParam = params.get('lijst');
      this.shared = sharedParam !== null;
      this.sharedName = cleanName(params.get('van') || '');
      this.sharedItems = this.shared ? this.parseShared(sharedParam) : [];

      if (this.el.eyebrow) {
        this.el.eyebrow.hidden = !this.shared;
        this.el.eyebrow.textContent = 'Met jou gedeeld';
      }

      if (this.shared && this.el.shared) {
        this.el.shared.hidden = false;
        this.el.sharedText.textContent = this.sharedName
          ? `${this.sharedName} heeft dit verlanglijstje met je gedeeld. Bestel direct of bewaar het in je eigen lijstje.`
          : 'Dit verlanglijstje is met je gedeeld. Bestel direct of bewaar het in je eigen lijstje.';
      }

      if (this.el.consent && this.listId() && this.publicKey()) this.el.consent.hidden = false;

      this.updateTitle();
      this.bind();
      this.render();
    }

    parseShared(value) {
      const seen = new Set();
      return String(value)
        .split(',')
        .map((part) => {
          const [handle, variant] = part.split(':');
          return { h: (handle || '').trim(), v: Number(variant) || 0 };
        })
        .filter((item) => HANDLE_PATTERN.test(item.h) && !seen.has(item.h) && seen.add(item.h))
        .slice(0, 60);
    }

    currentItems() {
      return this.shared ? this.sharedItems : this.W.read();
    }

    async fetchJson(url, accept) {
      const controller = 'AbortController' in window ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), 8000) : null;
      try {
        const response = await fetch(url, {
          headers: { Accept: accept || 'text/html' },
          credentials: 'same-origin',
          signal: controller ? controller.signal : undefined
        });
        if (response.status === 404) return { status: 'missing' };
        if (!response.ok) return { status: 'error' };
        const text = await response.text();
        try {
          return { status: 'ok', product: JSON.parse(text) };
        } catch (error) {
          return { status: 'invalid' };
        }
      } catch (error) {
        return { status: 'error' };
      } finally {
        if (timer) clearTimeout(timer);
      }
    }

    async loadProduct(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);

      const root = this.cfg.productsRoot || '/products/';
      const base = `${root}${encodeURIComponent(handle)}`;

      const usable = (data) => Boolean(data && typeof data === 'object' && data.title && Array.isArray(data.variants) && data.variants.length && data.variants.every((v) => v && v.id && typeof v.available === 'boolean'));

      let result = await this.fetchJson(`${base}?view=mb-wishlist-data`, 'text/html');
      if (result.status === 'ok' && !usable(result.product)) result = { status: 'invalid' };

      if (result.status === 'invalid' || result.status === 'error') {
        result = await this.fetchJson(`${base}.js`, 'application/json');
        if (result.status === 'ok' && !usable(result.product)) result = { status: 'invalid' };
        if (result.status === 'invalid') result = { status: 'error' };
      }

      if (result.status !== 'error') this.cache.set(handle, result);
      return result;
    }

    resolve(item, product) {
      const variants = product.variants || [];
      const variant = variants.find((v) => v.id === Number(item.v)) || variants.find((v) => v.available) || variants[0] || {};
      const multiple = variants.length > 1 && variant.title && variant.title !== 'Default Title';
      const image = (variant.featured_image && variant.featured_image.src) || product.featured_image || '';
      const url = (product.url || `/products/${product.handle}`) + (multiple && variant.id ? `?variant=${variant.id}` : '');

      const available = Boolean(variant.available);
      const stock = variant.stock || (available ? 'in_stock' : 'sold_out');
      const stockLabel = variant.stock_label || (available ? this.dataset.labelInStock : this.dataset.labelSoldOut) || '';

      return {
        handle: product.handle,
        productId: product.id,
        variantId: variant.id,
        title: product.title,
        variantTitle: multiple ? variant.title : '',
        url,
        image,
        price: Number(variant.price) || 0,
        compareAtPrice: Number(variant.compare_at_price) || 0,
        available,
        stock,
        stockLabel,
        type: product.type || '',
        qtyStep: quantityStep(product.quantity ? product.quantity.step : item.qs),
        qtySelect: product.quantity ? Boolean(product.quantity.select) : Boolean(item.qp)
      };
    }

    fromSnapshot(item) {
      if (!item || !item.t) return null;
      return {
        handle: item.h,
        productId: item.p,
        variantId: Number(item.v) || 0,
        title: item.t,
        variantTitle: item.vt || '',
        url: item.u || `/products/${item.h}`,
        image: item.i || '',
        price: Number(item.pr) || 0,
        compareAtPrice: Number(item.cp) || 0,
        available: Boolean(Number(item.v)),
        type: item.ty || '',
        qtyStep: quantityStep(item.qs),
        qtySelect: Boolean(item.qp),
        snapshot: true
      };
    }

    toStoredItem(row) {
      return {
        h: row.handle,
        p: row.productId,
        v: row.variantId,
        t: row.title,
        vt: row.variantTitle,
        u: row.url.split('?')[0],
        i: sizedImage(row.image, 600),
        pr: row.price,
        cp: row.compareAtPrice,
        ty: row.type,
        qs: row.qtyStep,
        qp: row.qtySelect ? 1 : 0
      };
    }

    updateTitle() {
      if (!this.el.title) return;
      const own = this.dataset;
      let text;
      if (this.shared) {
        text = this.sharedName ? own.headingSharedNamed.replace('[naam]', this.sharedName) : own.headingShared;
      } else {
        const name = this.W.getName();
        text = name ? own.headingNamed.replace('[naam]', name) : own.headingDefault;
        if (this.el.nameInput) this.el.nameInput.hidden = Boolean(name);
      }
      this.el.title.textContent = text;
    }

    async render() {
      const token = ++this.renderToken;
      const items = this.currentItems();

      if (!items.length) {
        this.showEmpty();
        return;
      }

      const needsLoading = items.some((item) => !this.cache.has(item.h));
      if (needsLoading && !this.rows) this.el.loading.hidden = false;

      let results;
      try {
        results = await Promise.all(items.map((item) => this.loadProduct(item.h)));
      } catch (error) {
        results = items.map(() => ({ status: 'error' }));
      }
      if (token !== this.renderToken) return;

      const rows = [];
      const missing = [];
      items.forEach((item, index) => {
        const result = results[index];
        let row = null;
        try {
          if (result.status === 'ok') row = this.resolve(item, result.product);
        } catch (error) {
          row = null;
        }
        if (!row && result.status !== 'missing') row = this.fromSnapshot(item);
        if (row) rows.push(row);
        if (result.status === 'missing') missing.push(item.h);
      });

      this.rows = rows;
      this.el.loading.hidden = true;

      if (!rows.length) {
        this.showEmpty();
      } else {
        this.showList(rows);
      }

      if (!this.shared && missing.length) {
        missing.forEach((handle) => this.W.remove(handle, { silent: true }));
      }
    }

    showEmpty() {
      this.el.loading.hidden = true;
      this.el.list.innerHTML = '';
      this.el.empty.hidden = false;
      this.el.aside.hidden = true;
      this.el.meta.hidden = true;
      this.el.layout.classList.add('is-empty');
      if (this.el.shared && this.shared) this.el.saveShared.hidden = true;
    }

    showList(rows) {
      const money = (cents) => this.W.formatMoney(cents);
      const availableRows = rows.filter((row) => row.available);
      const total = availableRows.reduce((sum, row) => sum + row.price * this.quantityFor(row), 0);
      const soldOut = rows.length - availableRows.length;

      this.el.empty.hidden = true;
      this.el.aside.hidden = false;
      this.el.layout.classList.remove('is-empty');

      this.el.meta.hidden = false;
      this.el.meta.textContent = `${rows.length} ${rows.length === 1 ? 'product' : 'producten'}`;

      this.el.summaryLabel.textContent = availableRows.length === 1 ? 'Totaal (1 product)' : `Totaal (${availableRows.length} producten)`;
      this.el.total.textContent = money(total);
      this.el.unavailableNote.hidden = soldOut === 0;
      this.el.unavailableNote.textContent = soldOut === 1
        ? '1 product is uitverkocht en telt niet mee.'
        : `${soldOut} producten zijn uitverkocht en tellen niet mee.`;
      this.el.addAll.disabled = availableRows.length === 0;

      this.el.list.innerHTML = rows.map((row) => this.rowHtml(row)).join('');
    }

    rowHtml(row) {
      const money = (cents) => escapeHtml(this.W.formatMoney(cents));
      const image = row.image
        ? `<img src="${escapeHtml(sizedImage(row.image, 300))}" srcset="${escapeHtml(sizedImage(row.image, 300))} 300w, ${escapeHtml(sizedImage(row.image, 600))} 600w" sizes="(max-width: 899px) 96px, 132px" alt="${escapeHtml(row.title)}" loading="lazy" width="300" height="300">`
        : '';
      const compare = row.compareAtPrice > row.price ? `<s>${money(row.compareAtPrice)}</s>` : '';
      const remove = this.shared
        ? ''
        : `<button type="button" class="mb-wl-item__remove" data-wl-remove="${escapeHtml(row.handle)}" aria-label="Verwijder ${escapeHtml(row.title)} uit je verlanglijstje">
             <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>
             <span>Verwijderen</span>
           </button>`;

      const picker = row.qtySelect && row.available;
      const quantity = this.quantityFor(row);
      const unit = picker ? '<span class="mb-wl-item__unit">per stuk</span>' : '';
      const stepper = picker
        ? `<div class="mb-wl-qty" role="group" aria-label="Aantal ${escapeHtml(row.title)}">
             <button type="button" class="mb-wl-qty__button" data-wl-qty="-1" aria-label="${row.qtyStep} minder" ${quantity <= row.qtyStep ? 'disabled' : ''}>
               <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 10h12"/></svg>
             </button>
             <output class="mb-wl-qty__value" data-wl-qty-value aria-live="polite">${quantity}</output>
             <button type="button" class="mb-wl-qty__button" data-wl-qty="1" aria-label="${row.qtyStep} meer" ${quantity + row.qtyStep > QUANTITY_MAX ? 'disabled' : ''}>
               <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M10 4v12M4 10h12"/></svg>
             </button>
           </div>`
        : '';

      return `
        <li class="mb-wl-item${picker ? ' mb-wl-item--quantity' : ''}" data-handle="${escapeHtml(row.handle)}">
          <a class="mb-wl-item__media" href="${escapeHtml(row.url)}" tabindex="-1" aria-hidden="true">${image}</a>
          <div class="mb-wl-item__info">
            <h2 class="mb-wl-item__title"><a href="${escapeHtml(row.url)}">${escapeHtml(row.title)}</a></h2>
            ${row.variantTitle ? `<p class="mb-wl-item__variant">${escapeHtml(row.variantTitle)}</p>` : ''}
            <div class="mb-wl-item__price"><span>${money(row.price)}</span>${compare}${unit}</div>
            ${picker ? `<p class="mb-wl-item__quantity-note" data-wl-qty-note ${this.quantityNote(row, quantity) ? '' : 'hidden'}>${escapeHtml(this.quantityNote(row, quantity))}</p>` : ''}
            ${row.snapshot ? '' : this.stockHtml(row)}
          </div>
          <div class="mb-wl-item__actions">
            <div class="mb-wl-item__buy">
              ${stepper}
              <button type="button" class="mb-wl-button mb-wl-button--primary" data-wl-add="${row.variantId}" ${row.available ? '' : 'disabled'}>
                ${row.available ? 'In mijn winkelwagen' : 'Uitverkocht'}
              </button>
            </div>
            ${remove}
          </div>
        </li>`;
    }

    quantityFor(row) {
      if (!row || !row.qtySelect) return row ? row.qtyStep || 1 : 1;
      return normalizeQuantity(this.quantities[row.handle], row.qtyStep);
    }

    quantityNote(row, quantity) {
      const total = this.W.formatMoney(row.price * quantity);
      const parts = [];
      if (row.qtyStep > 1) parts.push(`Per ${row.qtyStep} te bestellen`);
      if (quantity > 1) parts.push(`${quantity} stuks: ${total}`);
      return parts.join(' · ');
    }

    rowFor(element) {
      const item = element && element.closest('[data-handle]');
      return item && this.rows ? this.rows.find((row) => row.handle === item.dataset.handle) : null;
    }

    changeQuantity(button) {
      const row = this.rowFor(button);
      if (!row) return;
      const next = normalizeQuantity(this.quantityFor(row) + Number(button.dataset.wlQty) * row.qtyStep, row.qtyStep);
      this.quantities[row.handle] = next;
      try {
        localStorage.setItem(QUANTITY_KEY, JSON.stringify(this.quantities));
      } catch (error) {
      }
      const item = button.closest('[data-handle]');
      item.querySelector('[data-wl-qty-value]').textContent = next;
      item.querySelector('[data-wl-qty="-1"]').disabled = next <= row.qtyStep;
      item.querySelector('[data-wl-qty="1"]').disabled = next + row.qtyStep > QUANTITY_MAX;
      const note = item.querySelector('[data-wl-qty-note]');
      if (note) {
        note.textContent = this.quantityNote(row, next);
        note.hidden = !note.textContent;
      }
      this.updateTotal();
    }

    updateTotal() {
      const rows = (this.rows || []).filter((row) => row.available);
      this.el.total.textContent = this.W.formatMoney(rows.reduce((sum, row) => sum + row.price * this.quantityFor(row), 0));
    }

    stockHtml(row) {
      const tone = row.stock === 'in_stock' ? 'success' : (row.stock === 'sold_out' ? 'error' : 'warning');
      const iconTemplate = this.querySelector(`template[data-wl-icon="${tone === 'success' ? 'success' : 'warning'}"]`);
      const icon = iconTemplate ? iconTemplate.innerHTML : '';
      return `<span class="mb-wl-item__stock text-with-icon text-${tone}">${icon}<span class="mb-wl-item__stock-label">${escapeHtml(row.stockLabel)}</span></span>`;
    }

    bind() {
      document.addEventListener(this.W.CHANGE_EVENT, () => {
        if (!this.shared) this.render();
      });

      document.addEventListener(this.W.NAME_EVENT, () => this.updateTitle());

      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.cache.clear();
        this.rows = null;
        this.updateTitle();
        this.render();
      });

      this.el.list.addEventListener('click', (event) => {
        const addButton = event.target.closest('[data-wl-add]');
        const removeButton = event.target.closest('[data-wl-remove]');
        const quantityButton = event.target.closest('[data-wl-qty]');
        if (quantityButton) this.changeQuantity(quantityButton);
        if (addButton) this.addToCart(addButton);
        if (removeButton) this.removeItem(removeButton.dataset.wlRemove);
      });

      if (this.el.addAll) this.el.addAll.addEventListener('click', () => this.addAllToCart());
      if (this.el.share) this.el.share.addEventListener('click', () => this.share());
      if (this.el.saveShared) this.el.saveShared.addEventListener('click', () => this.saveShared());
      if (this.el.mailForm) this.el.mailForm.addEventListener('submit', (event) => this.mail(event));
    }

    async postCart(items) {
      const response = await fetch(this.cfg.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Toevoegen lukte niet.');
      return data;
    }

    refreshCart() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    async addToCart(button) {
      const label = button.textContent.trim();
      button.disabled = true;
      button.textContent = 'Toevoegen…';

      const quantity = this.quantityFor(this.rowFor(button));
      try {
        await this.postCart([{ id: Number(button.dataset.wlAdd), quantity }]);
        this.refreshCart();
        button.classList.add('is-added');
        button.textContent = 'Toegevoegd';
        // Net als "In mijn winkelwagen" op de productpagina: door naar de bijna-van-jou-pagina.
        // Heeft dat product daar niets te tonen, dan stuurt die pagina zelf door naar de winkelwagen.
        const row = this.rowFor(button);
        if (this.cfg.bijnaVanJou && row && row.handle) {
          const root = this.cfg.productsRoot || '/products/';
          window.location.href = `${root}${encodeURIComponent(row.handle)}?view=bijna-van-jou&variant=${Number(button.dataset.wlAdd)}`;
          return;
        }
        this.W.toast(quantity > 1 ? `${quantity} stuks toegevoegd aan je winkelwagen` : 'Toegevoegd aan je winkelwagen', { label: 'Naar winkelwagen', href: this.cfg.cartUrl || '/cart' });
        await wait(2500);
        button.classList.remove('is-added');
      } catch (error) {
        this.W.toast(error.message || 'Toevoegen lukte niet. Probeer het opnieuw.');
      }

      button.textContent = label;
      button.disabled = false;
    }

    async addAllToCart() {
      const rows = (this.rows || []).filter((row) => row.available);
      if (!rows.length) return;

      const button = this.el.addAll;
      const label = button.textContent.trim();
      button.disabled = true;
      button.textContent = 'Toevoegen…';

      let added = 0;
      try {
        await this.postCart(rows.map((row) => ({ id: row.variantId, quantity: this.quantityFor(row) })));
        added = rows.length;
      } catch (error) {
        for (const row of rows) {
          try {
            await this.postCart([{ id: row.variantId, quantity: this.quantityFor(row) }]);
            added += 1;
          } catch (innerError) {
          }
        }
      }

      if (added > 0) {
        window.location.href = this.cfg.cartUrl || '/cart';
        return;
      }

      this.W.toast('Toevoegen lukte niet. Probeer het opnieuw.');
      button.textContent = label;
      button.disabled = false;
    }

    removeItem(handle) {
      const before = this.W.read();
      const index = before.findIndex((item) => item.h === handle);
      const removed = before[index];
      const element = this.el.list.querySelector(`[data-handle="${CSS.escape(handle)}"]`);

      const finish = () => {
        this.W.remove(handle);
        this.W.toast('Verwijderd uit je verlanglijstje', {
          label: 'Ongedaan maken',
          onClick: () => this.W.restore(removed, index)
        });
      };

      if (element && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        element.classList.add('is-removing');
        setTimeout(finish, 220);
      } else {
        finish();
      }
    }

    async share() {
      const url = this.shared ? window.location.href : this.W.shareUrl();
      const name = this.W.getName();
      const title = name ? `Verlanglijstje van ${name}` : 'Mijn verlanglijstje bij Meubelista';

      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        try {
          await navigator.share({ title, text: title, url });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        this.flashShareLabel('Link gekopieerd');
      } catch (error) {
        window.prompt('Kopieer de link naar je verlanglijstje:', url);
      }
    }

    flashShareLabel(text) {
      const label = this.el.shareLabel;
      if (!label) return;
      const original = label.dataset.original || label.textContent;
      label.dataset.original = original;
      label.textContent = text;
      clearTimeout(this._shareTimer);
      this._shareTimer = setTimeout(() => { label.textContent = original; }, 2500);
    }

    saveShared() {
      const items = (this.rows || []).map((row) => this.toStoredItem(row));
      if (!items.length) return;
      this.W.addMany(items);
      window.location.href = this.cfg.pageUrl || '/pages/verlanglijstje';
    }

    publicKey() {
      const configured = this.dataset.klaviyoPublicKey;
      if (configured) return configured;
      const script = document.querySelector('script[src*="klaviyo.js?company_id="], script[src*="klaviyo.js?"]');
      if (!script) return '';
      try {
        return new URL(script.src, window.location.href).searchParams.get('company_id') || '';
      } catch (error) {
        return '';
      }
    }

    listId() {
      return this.dataset.klaviyoListId || '';
    }

    async klaviyoReady(timeout) {
      const started = Date.now();
      while (Date.now() - started < timeout) {
        const k = window.klaviyo;
        if (k && !Array.isArray(k) && typeof k.push === 'function') return true;
        await wait(150);
      }
      return false;
    }

    setMailStatus(text, type) {
      const status = this.el.mailStatus;
      status.textContent = text;
      status.classList.toggle('is-error', type === 'error');
      status.classList.toggle('is-success', type === 'success');
    }

    async mail(event) {
      event.preventDefault();
      const form = this.el.mailForm;
      const email = String(form.elements.email.value || '').trim();
      const typedName = this.el.nameInput && !this.el.nameInput.hidden ? cleanName(this.el.nameInput.value) : '';
      const rows = this.rows || [];

      if (!EMAIL_PATTERN.test(email)) {
        this.setMailStatus('Vul een geldig e-mailadres in.', 'error');
        form.elements.email.focus();
        return;
      }

      if (!rows.length) {
        this.setMailStatus('Je verlanglijstje is nog leeg.', 'error');
        return;
      }

      const submit = form.querySelector('button[type="submit"]');
      submit.disabled = true;
      this.setMailStatus('Bezig met versturen…');

      if (typedName) this.W.setName(typedName);
      const name = typedName || this.W.getName();

      const ready = await this.klaviyoReady(8000);
      if (!ready) {
        submit.disabled = false;
        this.setMailStatus('Versturen lukte niet. Schakel eventuele adblockers uit en probeer het opnieuw.', 'error');
        return;
      }

      const money = (cents) => this.W.formatMoney(cents);
      const availableTotal = rows.filter((row) => row.available).reduce((sum, row) => sum + row.price, 0);
      const shareUrl = this.shared ? window.location.href : this.W.shareUrl();

      const items = rows.map((row) => ({
        ProductName: row.title,
        VariantName: row.variantTitle,
        URL: this.W.absoluteUrl(row.url),
        ImageURL: sizedImage(this.W.absoluteUrl(row.image), 600),
        Price: money(row.price),
        PriceValue: row.price / 100,
        CompareAtPrice: row.compareAtPrice > row.price ? money(row.compareAtPrice) : '',
        Available: row.available
      }));

      const profile = { email };
      if (name) profile.first_name = name;

      this.W.identifyAndTrack(profile, 'Wishlist Emailed', {
        items,
        item_count: items.length,
        total: money(availableTotal),
        total_value: availableTotal / 100,
        wishlist_url: shareUrl,
        first_name: name || '',
        shared_list: this.shared,
        $value: availableTotal / 100
      });

      const consent = form.elements.newsletter;
      if (consent && consent.checked && this.listId() && this.publicKey()) {
        try {
          await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.publicKey())}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Verlanglijstje Meubelista',
                  profile: { data: { type: 'profile', attributes: profile } }
                },
                relationships: { list: { data: { type: 'list', id: this.listId() } } }
              }
            })
          });
        } catch (error) {
        }
      }

      submit.disabled = false;
      form.elements.email.value = '';
      this.setMailStatus(`Verstuurd! Je ontvangt je verlanglijstje binnen een paar minuten op ${email}.`, 'success');
    }
  }

  customElements.define('mb-wishlist-page', MbWishlistPage);
})();
